import { NocfoPermissionGroups } from '@root/constants'
import { IInvitation, IUsershipPerson } from '@query'
import { INocfoPermissionGroup } from '@query/constants'
import { matchPermissionPreset } from './PermissionPicker'

/** List API returns permissions; keep that typing local to this UI. */
export type InvitationListItem = IInvitation & { permissions?: string[] }

export type PeopleRoleBadgeType = 'info' | 'success' | 'default' | 'magic'

export type PeopleTableRow =
  | {
      kind: 'member'
      id: number
      key: string
      primary: string
      secondary: string | null
      avatarUrl: string
      roleId: string
      roleLabel: string
      roleBadgeType: PeopleRoleBadgeType
      usership: IUsershipPerson
    }
  | {
      kind: 'invitation'
      id: number
      key: string
      primary: string
      secondary: null
      roleId: string
      roleLabel: string
      roleBadgeType: PeopleRoleBadgeType
      invitation: InvitationListItem
    }

export const getUsershipPermissionIds = (usership: IUsershipPerson): string[] =>
  Object.entries(usership.permissions || {})
    .filter(([, permission]) => permission?.value)
    .map(([id]) => id)

export const getInvitationPermissionIds = (
  invitation: InvitationListItem,
  groups: INocfoPermissionGroup[] = []
): string[] => {
  if (invitation.permissions?.length) return invitation.permissions

  // List responses usually include expanded `permissions`, but fall back to the
  // preset matrix when only `permission_group` is present.
  if (invitation.permission_group && invitation.permission_group !== NocfoPermissionGroups.CUSTOM) {
    const group = groups.find(({ id }) => id === invitation.permission_group)
    if (group) return group.permissions.map(({ id }) => id)
  }

  return []
}

export const getPeopleRoleBadgeType = (groupId: string): PeopleRoleBadgeType => {
  if (groupId === NocfoPermissionGroups.ADMIN) return 'info'
  if (
    groupId === NocfoPermissionGroups.BOOKKEEPING ||
    groupId === NocfoPermissionGroups.INVOICING
  ) {
    return 'success'
  }
  if (groupId === NocfoPermissionGroups.CUSTOM) return 'magic'
  return 'default'
}

export const resolvePeopleRole = (
  permissionIds: string[],
  groups: INocfoPermissionGroup[]
): { groupId: string; label: string } => {
  const groupId = matchPermissionPreset(permissionIds, groups)
  const group = groups.find(({ id }) => id === groupId)

  return {
    groupId,
    label: group?.name ?? groupId
  }
}

interface BuildPeopleTableRowsArgs {
  userships: IUsershipPerson[]
  invitations: InvitationListItem[]
  groups: INocfoPermissionGroup[]
  createFullName: (usership: IUsershipPerson) => string
}

export const buildPeopleTableRows = ({
  userships,
  invitations,
  groups,
  createFullName
}: BuildPeopleTableRowsArgs): PeopleTableRow[] => {
  const memberRows: PeopleTableRow[] = userships.map(usership => {
    const permissionIds = getUsershipPermissionIds(usership)
    const role = resolvePeopleRole(permissionIds, groups)
    const fullName = createFullName(usership).trim()
    const primary = fullName || usership.email
    const secondary = fullName ? usership.email : null

    return {
      kind: 'member',
      id: usership.id,
      key: `member-${usership.id}`,
      primary,
      secondary,
      avatarUrl: usership.avatar_url,
      roleId: role.groupId,
      roleLabel: role.label,
      roleBadgeType: getPeopleRoleBadgeType(role.groupId),
      usership
    }
  })

  const invitationRows: PeopleTableRow[] = invitations.map(invitation => {
    const permissionIds = getInvitationPermissionIds(invitation, groups)
    const role = resolvePeopleRole(permissionIds, groups)

    return {
      kind: 'invitation',
      id: invitation.id,
      key: `invitation-${invitation.id}`,
      primary: invitation.email,
      secondary: null,
      roleId: role.groupId,
      roleLabel: role.label,
      roleBadgeType: getPeopleRoleBadgeType(role.groupId),
      invitation
    }
  })

  return [...memberRows, ...invitationRows]
}

export const filterPeopleTableRows = (rows: PeopleTableRow[], search: string): PeopleTableRow[] => {
  const query = search.trim().toLowerCase()
  if (!query) return rows

  return rows.filter(row => {
    const haystack = [row.primary, row.secondary, row.roleLabel]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return haystack.includes(query)
  })
}
